import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, SITE } from "@/data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-black/50 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 md:h-20 md:px-12 lg:px-24">
        <Link
          to="/"
          className="group flex items-center gap-2"
          data-testid="nav-logo"
        >
          <span className="font-cabinet text-lg font-bold tracking-tight text-primary-ink">
            Vivek Chandra
          </span>
          <span className="hidden h-1.5 w-1.5 rounded-full bg-accent transition-transform group-hover:scale-125 sm:block" />
        </Link>

        <ul className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-mono text-xs uppercase tracking-[0.15em] transition-colors hover:text-primary-ink ${
                    isActive ? "text-primary-ink" : "text-muted-ink"
                  }`
                }
              >
                {link.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="group hidden items-center gap-2 border border-white/15 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-primary-ink transition-colors hover:border-white/40 hover:bg-white/5 md:inline-flex"
          >
            Let's Talk
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>

          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center border border-white/10 text-primary-ink md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-white/10 bg-black/90 backdrop-blur-xl md:hidden"
            data-testid="mobile-menu"
          >
            <ul className="flex flex-col px-6 py-4">
              {NAV_LINKS.map((link) => (
                <li key={link.to} className="border-b border-white/5 last:border-0">
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block py-4 font-cabinet text-xl tracking-tight ${
                        isActive ? "text-primary-ink" : "text-secondary-ink"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}
              <li className="pt-4">
                <Link
                  to="/contact"
                  data-testid="mobile-nav-cta"
                  className="inline-flex w-full items-center justify-center gap-2 bg-accent px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] text-white"
                >
                  Let's Talk <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
